import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  AppRepository,
  LocalRepository,
  HttpRepository,
  CURRENT_USER,
  type Snapshot,
  type Command,
  type ThemeName,
  themes,
  type RecordStorage,
  applyCommand,
  type AITask,
  setCurrentUser,
} from '@ventureroom/core';
import { ZodError } from 'zod';
import { AppState, Linking, Platform } from 'react-native';
export interface Preferences {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
}
export type Destination =
  | 'discover'
  | 'connections'
  | 'ventures'
  | 'activity'
  | 'profile'
  | 'settings'
  | 'dashboard'
  | 'tools'
  | `person/${string}`
  | `venture/${string}`
  | `chat/${string}`
  | `meeting/${string}`
  | `boardroom/${string}`
  | `venturepreview/${string}`;
type DiscoveryFilters = { search: string; industry: string; role: string };
const defaultDiscoveryFilters = { search: '', industry: 'All interests', role: 'Anyone' };
type State = {
  workPrice: () => Promise<{
    available: boolean;
    amount?: number;
    currency?: string;
    interval?: string;
  }>;
  checkoutWork: (ventureId: string) => Promise<void>;
  ventureTabs: Record<string, string>;
  setVentureTab: (id: string, tab: string) => void;
  onboardingPending: boolean;
  needsRecoveryEmail: boolean;
  completeOnboarding: () => Promise<boolean>;
  accountRequest: (
    action:
      | 'register'
      | 'verify-email'
      | 'resend-verification'
      | 'forgot-password'
      | 'reset-password'
      | 'onboarding'
      | 'recovery-email',
    input: Record<string, unknown>,
  ) => Promise<{ message?: string; verificationRequired?: boolean; ok?: boolean } | null>;
  discoveryFilters: DiscoveryFilters;
  setDiscoveryFilters: (filters: DiscoveryFilters) => void;
  authenticated: boolean;
  allowDemo: boolean;
  authenticate: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  data: Snapshot | null;
  theme: ThemeName;
  ready: boolean;
  entered: boolean;
  busy: boolean;
  notice: string;
  error: string;
  actionFeedback: { id: string; error: string; success: string };
  beginAction: (id: string) => void;
  mode: 'local' | 'server';
  navigate: (to: Destination) => void;
  goBack: (fallback: Destination) => void;
  act: (c: Command, success?: string) => Promise<boolean>;
  refresh: () => Promise<void>;
  setTheme: (theme: ThemeName) => Promise<void>;
  enter: () => Promise<void>;
  startProfile: (input: unknown) => Promise<boolean>;
  reset: () => Promise<void>;
  pair: (url: string, token: string) => Promise<boolean>;
  disconnect: () => Promise<void>;
  announce: (s: string) => void;
  fail: (s: string) => void;
  reduceMotion: boolean;
  setReduceMotion: (b: boolean) => Promise<void>;
  tasks: AITask[];
  cancelTask: (id: string) => Promise<void>;
  artifact: (id: string) => Promise<Uint8Array>;
  haptic: () => void;
};
const Context = createContext<State | null>(null);
export function AppProvider({
  children,
  storage,
  preferences,
  navigate,
  goBack,
  haptic = () => undefined,
  allowDemo = false,
}: {
  children: ReactNode;
  storage: RecordStorage;
  preferences: Preferences;
  navigate: State['navigate'];
  goBack: State['goBack'];
  haptic?: () => void;
  allowDemo?: boolean;
}) {
  const local = useRef(new LocalRepository(storage));
  const repo = useRef<AppRepository>(local.current);
  const authURL =
    typeof window !== 'undefined' && Platform.OS === 'web'
      ? window.location.origin
      : 'https://ventureroom.procuraq.com';
  const sessionToken = useRef('');
  const [discoveryFilters, setDiscoveryFilters] = useState(defaultDiscoveryFilters);
  const [authenticated, setAuthenticated] = useState(false);
  const [onboardingPending, setOnboardingPending] = useState(false);
  const [needsRecoveryEmail, setNeedsRecoveryEmail] = useState(false);
  const [ventureTabs, setVentureTabs] = useState<Record<string, string>>({});
  const actionId = useRef('');
  const [actionFeedback, setActionFeedback] = useState({ id: '', error: '', success: '' });
  function beginAction(id: string) {
    actionId.current = id;
    setActionFeedback({ id, error: '', success: '' });
    setError('');
    setNotice('');
  }
  const [data, setData] = useState<Snapshot | null>(null),
    [theme, changeTheme] = useState<ThemeName>('warm'),
    [ready, setReady] = useState(false),
    [entered, setEntered] = useState(false),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(''),
    [error, setError] = useState(''),
    [mode, setMode] = useState<'local' | 'server'>('local'),
    [reduceMotion, changeMotion] = useState(false),
    [tasks, setTasks] = useState<AITask[]>([]);
  const activeTasks = tasks.some((task) => ['queued', 'analysing', 'working'].includes(task.status));
  const format = (e: unknown) =>
    e instanceof ZodError
      ? `${e.issues[0]?.path.join(' ')}: ${e.issues[0]?.message}`
      : e instanceof Error
        ? e.message
        : 'Something went wrong. Please try again.';
  const refresh = useCallback(async () => {
    const current = repo.current;
    try {
      const next = await current.read();
      if (current !== repo.current) return;
      setData(next);
      setError('');
    } catch (e) {
      if (current !== repo.current) return;
      if (e instanceof Error && e.message === 'Please sign in to VentureRoom.') {
        setAuthenticated(false);
        setData(null);
        setTasks([]);
      }
      setError(format(e));
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [savedTheme, entry, motion, url, token] = await Promise.all(
          ['theme', 'entered', 'motion', 'backend-url', 'backend-token'].map((k) =>
            preferences.get(k),
          ),
        );
        if (!mounted) return;
        changeTheme(savedTheme === 'dark' ? 'dark' : 'warm');
        setEntered(entry === 'yes');
        changeMotion(motion === 'reduced');
        // Remove legacy browser-side provider credentials; keys now remain server-side.
        await preferences.set('openai-key', '');
        const savedSession =
          Platform.OS === 'web' ? '' : ((await preferences.get('auth-token')) ?? '');
        if (!allowDemo) {
          try {
            const http = new HttpRepository(authURL, savedSession);
            const identity = await http.identity();
            setCurrentUser(identity.userId);
            setOnboardingPending(!!identity.onboardingPending);
            setNeedsRecoveryEmail(!!identity.needsRecoveryEmail);
            sessionToken.current = savedSession;
            repo.current = http;
            setMode('server');
            setAuthenticated(true);
            setEntered(true);
          } catch {
            setAuthenticated(false);
          }
        }
        if (allowDemo && url && token && repo.current === local.current) {
          const http = new HttpRepository(url, token);
          setCurrentUser((await http.identity()).userId);
          repo.current = http;
          setMode('server');
        }
        await refresh();
      } catch (e) {
        setError(format(e));
        setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [preferences, refresh, authURL, allowDemo]);
  useEffect(() => {
    if (!notice && !error) return;
    if (actionId.current) setActionFeedback({ id: actionId.current, error, success: notice });
  }, [notice, error]);
  async function act(c: Command, success = 'Saved to your workspace') {
    const current = repo.current;
    setBusy(true);
    setError('');

    // Server snapshots omit other users' private evidence. Only the server can validate
    // reputation/access against the full data; never reject it from a partial client snapshot.
    if (data && current.mode === 'local') {
      try {
        setData(applyCommand(data, c, CURRENT_USER));
      } catch (e) {
        setError(format(e));
        setBusy(false);
        return false;
      }
    }

    try {
      const next = await current.execute(c);
      if (current !== repo.current) return false;
      setData(next);
      setNotice(success);
      if (!reduceMotion) haptic();
      return true;
    } catch (e) {
      if (current !== repo.current) return false;
      try {
        const next = await current.read();
        if (current !== repo.current) return false;
        setData(next);
      } catch {
        /* Preserve the last usable snapshot if the connection is also unavailable. */
      }
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function setTheme(t: ThemeName) {
    await preferences.set('theme', t);
    changeTheme(t);
  }
  async function enter() {
    await preferences.set('entered', 'yes');
    setEntered(true);
  }
  async function startProfile(input: unknown) {
    if (entered || mode !== 'local') return false;
    setBusy(true);
    try {
      setData(await local.current.startProfile(input));
      await enter();
      setNotice('Your founder profile is ready');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function reset() {
    try {
      const next = await repo.current.reset();
      setData(next);
      setNotice('Demo data restored');
      navigate('discover');
    } catch (e) {
      setError(format(e));
    }
  }
  async function pair(url: string, token: string) {
    setBusy(true);
    try {
      const next = new HttpRepository(url.trim(), token.trim());
      const identity = await next.identity();
      const state = await next.read();
      setCurrentUser(identity.userId);
      await preferences.set('backend-url', url.trim());
      await preferences.set('backend-token', token.trim());
      repo.current = next;
      setData(state);
      setMode('server');
      setError('');
      setNotice('Connected to your local backend');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function disconnect() {
    if (authenticated) {
      await logout();
      return;
    }
    await preferences.set('backend-token', '');
    await preferences.set('backend-url', '');
    repo.current = local.current;
    setTasks([]);
    setMode('local');
    await refresh();
    setNotice('Your offline workspace is ready');
  }
  async function authenticate(username: string, password: string) {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(authURL + '/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(Platform.OS !== 'web' ? { 'X-VentureRoom-Native': 'true' } : {}),
        },
        body: JSON.stringify({ username, password }),
        signal: AbortSignal.timeout(15000),
      });
      const result = await response.json();
      if (!response.ok) throw Error(result.error ?? 'Could not sign in.');
      sessionToken.current = result.token ?? '';
      if (Platform.OS !== 'web') await preferences.set('auth-token', sessionToken.current);
      setCurrentUser(result.userId);
      repo.current = new HttpRepository(authURL, sessionToken.current);
      setData(await repo.current.read());
      setTasks([]);
      setMode('server');
      setAuthenticated(true);
      setOnboardingPending(!!result.onboardingPending);
      setNeedsRecoveryEmail(!!result.needsRecoveryEmail);
      setDiscoveryFilters(defaultDiscoveryFilters);
      setEntered(true);
      navigate('discover');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function accountRequest(
    action: Parameters<State['accountRequest']>[0],
    input: Record<string, unknown>,
  ) {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        authURL +
          (['onboarding', 'recovery-email'].includes(action)
            ? '/api/account/' + action
            : '/api/auth/' + action),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionToken.current ? { Authorization: 'Bearer ' + sessionToken.current } : {}),
          },
          body: JSON.stringify(input),
          signal: AbortSignal.timeout(25000),
        },
      );
      const result = await response.json();
      if (!response.ok) throw Error(result.error ?? 'Please try again.');
      if (action === 'recovery-email') setNeedsRecoveryEmail(false);
      return result as { message?: string; verificationRequired?: boolean; ok?: boolean };
    } catch (e) {
      setError(format(e));
      return null;
    } finally {
      setBusy(false);
    }
  }
  async function completeOnboarding() {
    if (!(await accountRequest('onboarding', {}))) return false;
    setOnboardingPending(false);
    return true;
  }
  async function logout() {
    try {
      const response = await fetch(authURL + '/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + sessionToken.current,
        },
        body: '{}',
        signal: AbortSignal.timeout(12000),
      });
      if (!response.ok) throw Error('Could not sign out. Try again.');
      await preferences.set('auth-token', '');
      await preferences.set('backend-token', '');
      await preferences.set('backend-url', '');
      sessionToken.current = '';
      setAuthenticated(false);
      setOnboardingPending(false);
      setEntered(false);
      setTasks([]);
      setData(null);
      setMode('local');
      repo.current = local.current;
      setCurrentUser('jamie');
      setDiscoveryFilters(defaultDiscoveryFilters);
      setNotice('');
      setError('');
      navigate('discover');
    } catch (e) {
      setError(format(e));
    }
  }
  async function cancelTask(id: string) {
    if (repo.current instanceof HttpRepository) {
      await repo.current.cancelTask(id);
      setTasks(await repo.current.tasks());
    }
  }
  async function artifact(id: string) {
    if (!(repo.current instanceof HttpRepository))
      throw Error('Connect to the backend to download this artifact.');
    return repo.current.artifact(id);
  }
  useEffect(() => {
    if (mode !== 'server') return;
    let disposed = false;
    let foreground = AppState.currentState === 'active';
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const current = repo.current;
        if (current instanceof HttpRepository) {
          const latest = await current.tasks();
          if (!disposed && current === repo.current) {
            setTasks(latest);
            await refresh();
          }
        }
      } catch {
        if (!disposed) await refresh();
      }
      if (!disposed) {
        const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        timer = setTimeout(() => void poll(), !foreground || hidden ? 30000 : activeTasks ? 2500 : 15000);
      }
    };
    const appState = AppState.addEventListener('change', (state) => {
      foreground = state === 'active';
      if (foreground) {
        clearTimeout(timer);
        void poll();
      }
    });
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
      appState.remove();
    };
  }, [mode, refresh, activeTasks]);
  async function setReduceMotion(b: boolean) {
    await preferences.set('motion', b ? 'reduced' : 'normal');
    changeMotion(b);
  }
  const workPrice = useCallback(
    () =>
      repo.current instanceof HttpRepository
        ? repo.current.workPrice()
        : Promise.resolve({ available: false }),
    [],
  );
  return (
    <Context.Provider
      value={{
        discoveryFilters,
        workPrice,
        checkoutWork: async (id) => {
          try {
            if (!(repo.current instanceof HttpRepository))
              throw Error('Premium checkout is available on the connected app.');
            const { url } = await repo.current.checkoutWork(id);
            await Linking.openURL(url);
          } catch (e) {
            setError(format(e));
          }
        },
        ventureTabs,
        setVentureTab: (id, tab) => setVentureTabs((previous) => ({ ...previous, [id]: tab })),
        accountRequest,
        onboardingPending,
        needsRecoveryEmail,
        completeOnboarding,
        setDiscoveryFilters,
        authenticated,
        allowDemo,
        authenticate,
        logout,
        data,
        theme,
        ready,
        entered,
        busy,
        notice,
        error,
        actionFeedback,
        beginAction,
        mode,
        navigate,
        goBack,
        act,
        refresh,
        setTheme,
        enter,
        startProfile,
        reset,
        pair,
        disconnect,
        announce: setNotice,
        fail: setError,
        reduceMotion,
        setReduceMotion,
        tasks,
        cancelTask,
        artifact,
        haptic,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useApp() {
  const value = useContext(Context);
  if (!value) throw Error('AppProvider is required');
  return value;
}
export function useTheme() {
  return themes[useApp().theme];
}
export function useData() {
  const { data } = useApp();
  if (!data) throw Error('Workspace is not ready');
  return data;
}
export function useMe() {
  return useData().profiles.find((p) => p.id === CURRENT_USER)!;
}
